// realTime.js

import {
  ref,
  set,
  push,
  update,
  remove,
  onValue,
  serverTimestamp,
  getDatabase,
  onChildAdded,
} from "firebase/database";
import { database } from "./config.js";
import { useEffect, useState } from "react";
/**
 * Writes data to a specified path in the Realtime Database.
 * This will overwrite any existing data at this path.
 * @param {string} path - The path to the data, e.g., 'users/userId/profile'.
 * @param {object} data - The data object to write.
 */
export function writeData(path, data) {
  const dbRef = ref(database, path);
  set(dbRef, data)
    .then(() => {
      console.log("Data written successfully!");
    })
    .catch((error) => {
      console.error("Data could not be written: " + error);
    });
}

/**
 * Appends new data to a list under a specified path with a unique, auto-generated key.
 * This is useful for creating a list of items like messages or posts.
 * @param {string} path - The path to the list, e.g., 'chats/general/messages'.
 * @param {object} data - The data object to add.
 */
export function pushDataToList(path, data) {
  const listRef = ref(database, path);
  const newPostRef = push(listRef);
  set(newPostRef, data)
    .then(() => {
      console.log("Data pushed successfully!");
    })
    .catch((error) => {
      console.error("Data could not be pushed: " + error);
    });
}

export function updateData(path, updates) {
  const dbRef = ref(database, path);
  update(dbRef, updates)
    .then(() => {
      console.log("Data updated successfully!");
    })
    .catch((error) => {
      console.error("Data could not be updated: " + error);
    });
}

/**
 * Removes data from a specified path in the Realtime Database.
 * @param {string} path - The path to the data to remove, e.g., 'users/userId/profile'.
 */
export function removeData(path) {
  const dbRef = ref(database, path);
  remove(dbRef)
    .then(() => {
      console.log("Data removed successfully!");
    })
    .catch((error) => {
      console.error("Data could not be removed: " + error);
    });
}

/**
 * Sets up a real-time listener for data at a specified path.
 * The callback function is triggered initially with the data and then every time the data changes.
 * @param {string} path - The path to the data to listen to.
 * @param {function} callback - A function to be called with the new data snapshot.
 * @returns {function} A function to unsubscribe the listener.
 */
export function listenForData(path, callback) {
  const dbRef = ref(database, path);
  const unsubscribe = onValue(
    dbRef,
    (snapshot) => {
      const data = snapshot.val();
      callback(data);
    },
    (error) => {
      console.error("The read failed: " + error.name);
    }
  );
  return unsubscribe;
}

export async function recordBookRead(userId, name, book, theme) {
  const db = getDatabase();
  const newReadRef = push(ref(db, "bookreads"));
  await set(newReadRef, {
    userId,
    name,
    book,
    theme,
    timestamp: Date.now(), // or serverTimestamp() if using Firestore-like timestamps
  });
}

export function useBookReadListener(currentUserId) {
  const [latestRead, setLatestRead] = useState(null);

  useEffect(() => {
    const db = getDatabase();
    const bookreadsRef = ref(db, "bookreads");

    const unsubscribe = onChildAdded(bookreadsRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) return;

      const now = Date.now();
      const age = now - data.timestamp;

      // 🧩 2-minute freshness rule
      if (age > 2 * 60 * 1000) return;

      // 🚫 Don’t announce for the same user
      if (data.userId === currentUserId) return;

      // ✅ Otherwise, trigger announcement
      setLatestRead(data);
    });

    return () => unsubscribe();
  }, [currentUserId]);

  return latestRead;
}
